import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const steps = [
    {
        num: "01",
        title: "System Architecture",
        desc: "We define the state management layer, routing protocol, and native bridging logic before a single pixel is painted.",
        viz: (
            <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_20s_linear_infinite]">
                <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="1" className="opacity-20" />
                <path d="M50 10 L50 90 M10 50 L90 50" stroke="currentColor" strokeWidth="0.5" className="opacity-40" />
                <rect x="25" y="25" width="50" height="50" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" strokeWidth="1" className="text-accent border-accent" />
            </svg>
        )
    },
    {
        num: "02",
        title: "Pixel Perfect Synthesis",
        desc: "UI components are engineered to perfectly match design specifications, ensuring 120hz scroll performance.",
        viz: (
            <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
                <div className="grid grid-cols-5 grid-rows-5 gap-2 w-3/4 h-3/4 opacity-20">
                    {Array.from({ length: 25 }).map((_, i) => <div key={i} className="bg-current rounded-sm" />)}
                </div>
                <div className="absolute top-0 w-full h-[2px] bg-accent shadow-[0_0_15px_rgba(138,53,144,0.8)] animate-[scan_3s_ease-in-out_infinite_alternate]" />
            </div>
        )
    },
    {
        num: "03",
        title: "Native Compilation",
        desc: "Rigorous testing across physical iOS and Android devices ensures zero jank and absolute stability.",
        viz: (
            <svg viewBox="0 0 100 50" className="w-full h-full">
                <path
                    d="M0 25 L30 25 L35 10 L45 40 L55 5 L65 35 L70 25 L100 25"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray="200"
                    className="animate-[dash_3s_linear_infinite] text-accent"
                />
            </svg>
        )
    }
];

export default function Protocol() {
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        const ctx = gsap.context(() => {
            const cards = gsap.utils.toArray('.protocol-card');

            cards.forEach((card, i) => {
                if (i === cards.length - 1) return; // Last card doesn't scale down

                ScrollTrigger.create({
                    trigger: card,
                    start: "top top",
                    endTrigger: cards[i + 1],
                    end: "top top",
                    pin: true,
                    pinSpacing: false,
                });

                gsap.to(card, {
                    scale: 0.9,
                    opacity: 0.5,
                    filter: "blur(10px)",
                    ease: "none",
                    scrollTrigger: {
                        trigger: cards[i + 1],
                        start: "top bottom",
                        end: "top top",
                        scrub: true,
                    }
                });
            });
        }, containerRef);
        return () => ctx.revert();
    }, []);

    return (
        <section ref={containerRef} className="bg-background relative" id="protocol">
            <style>{`
        @keyframes scan { from { transform: translateY(0); } to { transform: translateY(100vh); } }
        @keyframes dash { from { stroke-dashoffset: 200; } to { stroke-dashoffset: 0; } }
      `}</style>

            {steps.map((step, idx) => (
                <div
                    key={idx}
                    className="protocol-card h-[100dvh] w-full flex items-center justify-center p-6 md:p-16 z-10 origin-top bg-background"
                >
                    <div className="max-w-6xl w-full grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center">

                        {/* Left Content */}
                        <div className="flex flex-col">
                            <span className="font-data text-accent font-bold text-lg md:text-xl tracking-widest mb-4">PHASE {step.num}</span>
                            <h2 className="font-heading font-bold text-4xl md:text-6xl tracking-tight text-primary mb-6">
                                {step.title}
                            </h2>
                            <p className="font-subheading text-dark/70 text-lg md:text-xl max-w-md">
                                {step.desc}
                            </p>
                        </div>

                        {/* Right Visualization */}
                        <div className="w-full aspect-square md:aspect-[4/3] bg-dark rounded-[2rem] border border-dark/10 shadow-2xl overflow-hidden p-8 flex items-center justify-center text-background">
                            <div className="w-2/3 h-2/3">
                                {step.viz}
                            </div>
                        </div>

                    </div>
                </div>
            ))}
        </section>
    );
}
